import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { yupResolver } from '@/gf/lib/yupResolver'
import React from 'react'
import { useForm, useWatch } from 'react-hook-form'

import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { SEARCH_BUYERS } from '@/gf/queries/users'
import { ShippingOptions } from '../../_gen/gql'
import useBuyers from '../../hooks/useBuyers'
import useSession from '../../hooks/useSession'
import { CreateRfqForm, CreateRfqFormSchema } from './form'

import AttachmentM from '@/gf/modules/Attachment'
import Phone from '@/gf/modules/Phone'
import User from '@/gf/modules/User'

import Address from '@/gf/components/Address'
import AddressModalForm from '@/gf/components/AddressModalForm'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Layout from '@/gf/components/Layout'
import Page from '@/gf/components/Page'
import PartRequestsInput from '@/gf/components/PartRequestsInput'
import Spinner from '@/gf/components/Spinner'
import TextField from '@/gf/components/TextField'
import AddPlatformUserModal from '../../components/AddPlatformUserModal'
import Frame from '../../components/Frame'
import SupplierAutocomplete from '../../components/SupplierAutocomplete'
import AdminMachineCard from './AdminMachineCard'
import RequestCard from './RequestCard'

import UppyFilesManager from '@/gf/components/inputs/UppyFilesManager'
import { SupplierUser } from '../../../types'

interface BuyerOption {
  value: string
  label: string
}

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [
    { name: 'RFQs', href: '/rfqs' },
    { name: 'Create RFQ', href: `/rfqs/create` },
  ],
}

const shouldValidate = (formStateErrors: Record<string, unknown>) =>
  Object.keys(formStateErrors).length !== 0

const CreateRequestForQuote = () => {
  const [_msgs, msgsMgr] = useMsgs()
  const { user: loggedInUser } = useSession()
  const [spinnerLive, spinner] = useToggle()
  const [shippingAddressModalOpen, shippingAddressModalToggler] = useToggle()
  const [addUserModalOpen, addUserModalToggler] = useToggle()
  const [imageUrls, setImageUrls] = React.useState<string[]>([])
  const allBuyers = useBuyers()

  const [allBuyersState, setAllBuyersState] = React.useState<
    {
      id: string
      name: string
      email: string
      phoneNumber: string
      organization: { id: string }
    }[]
  >(allBuyers)

  const [allUsers, setAllUsers] = React.useState<SupplierUser[]>([])
  const [selectedBuyers, setSelectedBuyers] = React.useState<BuyerOption[]>([])
  const [selectedBuyerAccountId, setSelectedBuyerAccountId] = React.useState<string>('')
  const createRfqForm = useForm<CreateRfqForm>({
    shouldUnregister: true,
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      partsRequest: '',
      vendorIds: [],
      shippingLocationIds: [],
      shippingAddress: {},
      shippingOption: ShippingOptions.None,
      partRequestInputs: [],
    },
    resolver: yupResolver(CreateRfqFormSchema),
  })

  // Format/validate phone number
  const phoneNumber = useWatch({ control: createRfqForm.control, name: 'phoneNumber' })
  const username = useWatch({ control: createRfqForm.control, name: 'name' })
  const userEmail = useWatch({ control: createRfqForm.control, name: 'email' })
  const shippingAddress = useWatch({ control: createRfqForm.control, name: 'shippingAddress' })
  const categoryId = useWatch({ control: createRfqForm.control, name: 'categoryId' })

  React.useEffect(() => {
    if (phoneNumber) createRfqForm.setValue('phoneNumber', Phone.format(phoneNumber))
  }, [phoneNumber])

  // React hook form didn't like setting the default partRequestInput in defaultValues, so set the default value here
  // From their docs: "Avoid including custom object into the defaultValues ... those will lead to unexpected result during internal object clone process."
  React.useEffect(
    () => createRfqForm.setValue('partRequestInputs', [PartRequestsInput.defaultPartRequestInput]),
    []
  )
  React.useEffect(() => {
    setAllBuyersState(allBuyers)
  }, [allBuyers])
  const buyerOptions = allBuyers.map((u) => ({
    value: u.id,
    label: `${u.name} ${u.email}`,
  }))

  const rightContent = <Button onClick={addUserModalToggler.on}>Add User</Button>

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page title="Create RFQ" previousPage="/rfqs" titleRightContent={rightContent}>
        {spinnerLive ? (
          <Spinner />
        ) : (
          <>
            <AddressModalForm
              title="Edit shipping address"
              open={shippingAddressModalOpen}
              onClose={shippingAddressModalToggler.off}
              onSubmit={(newAddress) => {
                shippingAddressModalToggler.off()
                createRfqForm.setValue('shippingAddress', newAddress, {
                  shouldValidate: shouldValidate(createRfqForm.formState.errors),
                })
              }}
              initialAddress={shippingAddress}
            />
            <AddPlatformUserModal open={addUserModalOpen} onClose={addUserModalToggler.off} />
            <Layout>
              <Layout.Section type="primary">
                <Card title="Contact information">
                  <Card.Section>
                    <div className="space-y-2">
                      {User.isAdmin(loggedInUser) && (
                        <SupplierAutocomplete
                          label="User"
                          defaultOptions={buyerOptions}
                          allData={allUsers}
                          query={SEARCH_BUYERS}
                          selected={selectedBuyers}
                          reactSelectOptions={{ isMulti: false }}
                          onChange={(option) => {
                            setSelectedBuyers([option])
                            const user = allBuyersState.find((b) => b.id === option.value)
                            if (user) {
                              createRfqForm.setValue('name', user.name)
                              createRfqForm.setValue('email', user.email)
                              createRfqForm.setValue('phoneNumber', user.phoneNumber)
                              createRfqForm.setValue('onBehalfOfUserId', user.id)
                              setSelectedBuyerAccountId(user.organization.id)
                            }
                          }}
                          transformation={(response) => {
                            const buyerUsers = response.data.searchBuyers.map((u) => ({
                              value: u.id,
                              label: `${u.name} ${u.email}`,
                            }))

                            setAllBuyersState(response.data.searchBuyers)
                            setAllUsers(buyerUsers)

                            return buyerUsers
                          }}
                        />
                      )}
                      {!phoneNumber && !username && !userEmail ? null : (
                        <div className="pt-1">
                          <TextField
                            type="tel"
                            label="Phone Number"
                            errors={createRfqForm.formState.errors?.phoneNumber?.message}
                            {...createRfqForm.register('phoneNumber')}
                          />
                        </div>
                      )}
                    </div>
                  </Card.Section>
                </Card>

                <AdminMachineCard
                  accountId={selectedBuyerAccountId}
                  createRfqForm={createRfqForm}
                  shouldValidate={shouldValidate}
                />

                <RequestCard
                  createRfqForm={createRfqForm}
                  shouldValidate={shouldValidate}
                  categoryId={categoryId}
                  spinner={spinner}
                  imageUrls={imageUrls}
                />
              </Layout.Section>
              <Layout.Section type="secondary">
                <Card
                  title="Shipping address"
                  headerButton={
                    <button
                      className="text-sm text-blue-500 underline"
                      type="button"
                      onClick={shippingAddressModalToggler.on}
                    >
                      Edit
                    </button>
                  }
                >
                  <Card.Section>
                    {shippingAddress ? (
                      <Address className="text-sm text-gray-900" address={shippingAddress} />
                    ) : (
                      <p className="text-sm text-gray-500">No shipping address</p>
                    )}
                    {createRfqForm.formState.errors.shippingAddress && (
                      <div className="m-0.5">
                        <ExclamationCircleIcon
                          className="inline-block mr-1 h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                        <span className="text-sm text-red-500">
                          Shipping Address is a required field
                        </span>
                      </div>
                    )}
                  </Card.Section>
                </Card>
                <Card title="Images">
                  <Card.Section>
                    <UppyFilesManager
                      fileUrls={imageUrls}
                      uploadContext="supplier_create_rfq_images"
                      onAdd={(addImageUrls) => {
                        setImageUrls((prev) => [...prev, ...addImageUrls])
                        return Promise.resolve()
                      }}
                      onRemove={(removeImageUrl) => {
                        setImageUrls((prev) =>
                          prev.filter((imageUrl) => imageUrl !== removeImageUrl)
                        )
                        return Promise.resolve()
                      }}
                      onError={(error) => msgsMgr.add(error, 'negative')}
                      allowedFileTypes={AttachmentM.defaultAllowedFileTypes}
                    />
                  </Card.Section>
                </Card>
              </Layout.Section>
            </Layout>
          </>
        )}
      </Page>
    </Frame>
  )
}

export default CreateRequestForQuote
